import { t } from '@lingui/macro'
import { Navigate, useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, Padding } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { SafeArea } from '@/@bothrs-ui/web/components/safe-area'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { mixpanel } from '@/mixpanel'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { useBiometrics } from '../../hooks/useBiometrics'
import { useBiometricsPermission } from '../../hooks/useBiometricsPermission'
import { BiometricsAppPermission } from '../../state/biometrics.atom'

import type { FC } from 'react'

export const BiometricsPermissionPage: FC = () => {
  // -- Hooks --
  const theme = useTheme()
  const navigate = useNavigate()
  const tenantHref = useTenantHref()
  const homePath = tenantHref('/')

  const { requestBiometricPermission } = useBiometrics()
  const { updateBiometricsPermission } = useBiometricsPermission()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  // -- Functions --
  const handleEnableBiometrics = async () => {
    const permissionGranted = await requestBiometricPermission()

    mixpanel.track('biometrics_permission', {
      status: 'granted',
      os_permission_granted: permissionGranted,
    })

    updateBiometricsPermission(permissionGranted ? BiometricsAppPermission.GRANTED : BiometricsAppPermission.DENIED)
    navigate(homePath)
  }

  const handleSkipBiometrics = () => {
    const biometricPermission = BiometricsAppPermission.SKIPPED
    mixpanel.track('biometrics_permission', {
      status: biometricPermission,
    })

    updateBiometricsPermission(biometricPermission)

    navigate(homePath)
  }

  if (isLoggedInForMandatee) {
    return <Navigate to='/home' />
  }

  return (
    <SafeArea>
      <ContentPageLayout noScroll>
        {/* Container */}
        <FlexColumn justifyContent='space-between' style={{ height: '100%' }} gap={theme.UI.Spacing.Space2}>
          {/* Content */}
          <FlexColumn flexGrow={1} gap={theme.UI.Spacing.Space2} justifyContent='center' alignItems='center'>
            <StBiometricsImage src='/img/svg/biometrics-info.svg' />

            <H2 textAlign='center'>
              {t({
                id: 'biometric-info.title',
                message: 'Wil je sneller kunnen aanmelden?',
              })}
            </H2>

            <P textAlign='center' type='BodyMediumRegular'>
              {t({
                id: 'biometric-info.description',
                message:
                  'Gebruik de vingerafdruk of gezichtsherkenning op je toestel om snel en veilig aan te melden. Je kan dit ook later in je instellingen activeren of wijzigen.',
              })}
            </P>
          </FlexColumn>

          {/* CTA Container */}
          <Padding py={theme.UI.Spacing.Space4}>
            <FlexColumn flexShrink={1} gap={theme.UI.Spacing.Space2}>
              <Button variant='Primary' onClick={handleEnableBiometrics}>
                {t({
                  id: 'biometric-info.enable',
                  message: 'Toestemming geven',
                })}
              </Button>

              <Button variant='Secondary' onClick={handleSkipBiometrics}>
                {t({
                  id: 'biometric-info.skip',
                  message: 'Overslaan',
                })}
              </Button>
            </FlexColumn>
          </Padding>
        </FlexColumn>
      </ContentPageLayout>
    </SafeArea>
  )
}

const StBiometricsImage = styled.img`
  object-fit: contain;
  height: 35vh;
`
